import {useCallback, useEffect, useRef, useState} from 'react';
import {useBlocker} from 'react-router-dom';

import {ConfirmLeaveModal} from './ConfirmLeaveModal';

export function useConfirmLeave(predicate: () => boolean) {
  const [open, setOpen] = useState(false);
  const blocker = useBlocker(predicate);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const confirmationPromise = useRef<(confirmed: boolean) => void>();

  const confirm = useCallback<(callback: () => void) => Promise<void>>(
    (callback) => {
      if (predicate()) {
        setOpen(true);
        return new Promise((resolve) => {
          confirmationPromise.current = (confirmation: boolean) => {
            if (confirmation) {
              callback();
            }
            close();
            confirmationPromise.current = undefined;
            resolve();
          };
        });
      } else {
        callback();
        return Promise.resolve();
      }
    },
    [close, predicate],
  );

  const renderConfirmationModal = useCallback(() => {
    function handleConfirmationModalConfirmation() {
      if (typeof confirmationPromise.current === 'function') {
        confirmationPromise.current(true);
      }
    }

    function handleConfirmationModalCancel() {
      if (typeof confirmationPromise.current === 'function') {
        blocker.reset?.();
        confirmationPromise.current(false);
      }
    }

    if (!open) {
      return null;
    }

    return (
      <ConfirmLeaveModal
        open={open}
        onConfirm={handleConfirmationModalConfirmation}
        onCancel={handleConfirmationModalCancel}
      />
    );
  }, [open, blocker]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      confirm(blocker.proceed);
    }
  }, [blocker, confirm]);

  useEffect(() => {
    if (!predicate()) {
      return;
    }

    // handle tab/window closing
    const handleUnload = (event: Event) => {
      event.returnValue = true;
      return true;
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [predicate]);

  return {
    renderConfirmationModal,
    confirm,
  };
}
